import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Inject } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { IDialogData } from "../../viewer-components/interfaces/i-dialog-data";
import { BaseViewerService } from "../../viewer-components/services/internal-services/viewer-services/base-viewer.service";
import { saveAs } from "file-saver";
import { ViewerConstants } from "../../viewer-components/../shared/model/viewer-constants";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../viewer-components/../shared/model/google-analytics-constants";

import ShapeFileUtils from "@vision/core-map-api/src/utils/ShapeFileUtils";
import { Tools } from "@vision/core-map-api/src/constants/CoreMapConstants";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";
export abstract class ExportFeaturesComponent {
  viewerService: BaseViewerService;

  googleAnalyticsService: GoogleAnalyticsService;

  features: any;

  formats = [
    { format: "shp", text: "Shapefile (.zip)" },
    { format: "kml/kmz", text: "KML/KMZ (.kml/.kmz)" },
    { format: "geojson", text: "GeoJSON (.json/geojson)" },
    { format: "wkt", text: "WKT (.wkt)" },
  ];

  geometryTypes = [
    { type: "Polygon", text: "Polygon" },
    { type: "LineString", text: "Line" },
    { type: "Point", text: "Point" },
    { type: null, text: "Export all types as separate files" },
  ];

  exportFeaturesForm = new FormGroup({
    format: new FormControl(this.formats[0]),
    geometryType: new FormControl(this.geometryTypes[0]),
  });

  multipleGeometryTypes: boolean;

  protected constructor(@Inject(MAT_DIALOG_DATA) protected data: IDialogData) {
    this.features = this.formatFeatures(data.features);
    this.multipleGeometryTypes = this.features.some(
      (feature) => feature.getGeometry().getType() !== this.features[0].getGeometry().getType(),
    );
    this.updateGeometryTypes();
    this.viewerService = this.data.viewerService;
    this.googleAnalyticsService = this.data.googleAnalyticsService;
  }

  formatFeatures(features: any[]): any[] {
    let featuresFormatted = features;
    if (this.data.viewerService.isDiscreteEnabled()) {
      featuresFormatted = featuresFormatted.map((f) => {
        const newFeature = f.clone();
        if (f.originalGeometry) {
          if (f.originalGeometry.getType() === ViewerConstants.GEOMETRY_TYPE.GeometryCollection) {
            newFeature.setGeometry(
              f.originalGeometry.getGeometries().find((g) => g.getType() === ViewerConstants.GEOMETRY_TYPE.Polygon),
            );
            if (f.get("measurement")?.tool === Tools.MEASURE_CIRCLE) {
              newFeature.set("isCircle", true);
            }
          } else {
            newFeature.setGeometry(f.originalGeometry.clone());
          }
        }
        newFeature.setGeometryName("geometry");
        return newFeature;
      });
    } else {
      featuresFormatted = featuresFormatted.map((f) => {
        const newFeature = f.clone();
        if (f.getGeometry().getType() === ViewerConstants.GEOMETRY_TYPE.GeometryCollection) {
          newFeature.setGeometry(
            f
              .getGeometry()
              .getGeometries()
              .find((g) => g.getType() === ViewerConstants.GEOMETRY_TYPE.Polygon),
          );
          if (f.get("measurement")?.tool === Tools.MEASURE_CIRCLE) {
            newFeature.set("isCircle", true);
          }
        }
        return newFeature;
      });
    }
    return featuresFormatted;
  }

  exportLayer() {
    let data;
    let type;
    let extension;
    const current = new Date();
    const timestamp = current.toISOString();
    switch (this.exportFeaturesForm.get("format").value.format) {
      case "shp":
        if (this.multipleGeometryTypes) {
          const geometryType = this.exportFeaturesForm.get("geometryType").value.type;
          this.downloadShapefile(this.features, geometryType);
        } else {
          this.downloadShapefile(this.features);
        }

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_DOWNLOAD + "shapefile",
          GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
          true,
        );
        break;
      case "kml/kmz":
        const kmlWriter = this.viewerService.getCoreMap().getKMLFormatter();
        data = kmlWriter.writeFeatures(this.features, {
          featureProjection: ViewerConstants.PROJECTIONS.WebMercator,
        });
        type = "application/xml";
        extension = ".kml";
        saveAs(new Blob([data], { type }), "drawinglayer" + timestamp + extension);

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_DOWNLOAD + "KML/KMZ",
          GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
          true,
        );
        break;
      case "geojson":
        const geoJSONWriter = this.viewerService.getCoreMap().getGeoJSONFormatter();
        data = geoJSONWriter.writeFeatures(this.features, ViewerConstants.PROJECTIONS.WebMercator);
        type = "application/json";
        extension = ".geojson";
        saveAs(new Blob([data], { type }), "drawinglayer" + timestamp + extension);

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_DOWNLOAD + "GeoJSON",
          GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
          true,
        );
        break;
      case "wkt":
        const wktWriter = this.viewerService.getCoreMap().getWKTFormatter();
        data = wktWriter.writeFeatures(this.features, ViewerConstants.PROJECTIONS.WebMercator);
        type = "text/plain";
        extension = ".wkt";
        saveAs(new Blob([data], { type }), "drawinglayer" + timestamp + extension);

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.EXPORT_GEOMETRY_DOWNLOAD + "WKT",
          GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
          true,
        );
        break;
    }
  }

  downloadShapefile(features: any[], geometryType: string = null) {
    const current = new Date();
    const timestamp = current.toISOString();
    let filterFeatures = features;
    if (geometryType) {
      filterFeatures = features.filter((f) => f.getGeometry().getType() === geometryType);
    }
    const writer = this.viewerService.getCoreMap().getGeoJSONFormatter();
    let data = writer.writeFeatures(filterFeatures, ViewerConstants.PROJECTIONS.WebMercator);

    data = JSON.parse(data);

    const options = {
      types: {
        multipoint: "multipoints",
        polyline: "polylines",
        point: "points",
        polygon: "polygons",
        line: "lines",
      },
    };

    data.features.forEach((feat) => {
      if (feat.geometry.type === "Point" && feat.properties.type === "measurement") {
        this.setShapefileProperties(feat.properties);
      }
    });

    ShapeFileUtils.zip(data, options).then((content) => {
      // see FileSaver.js
      saveAs(content, "drawinglayer" + timestamp + ".zip");
    });
  }

  setShapefileProperties(properties: any) {
    if (properties.measurement.info.source === "dtm") {
      properties.DTM_VAL = properties.measurement.elevation.value;
      properties.DTM_UNIT = properties.measurement.elevation.unit;
    } else if (properties.measurement.info.survey === "dsm") {
      properties.DSM_VAL = properties.measurement.elevation.value;
      properties.DSM_UNIT = properties.measurement.elevation.unit;
    }
  }

  updateGeometryTypes() {
    if (this.multipleGeometryTypes) {
      const uniqueTypes = [...new Set(this.features.map((feature) => feature.getGeometry().getType()))];

      this.geometryTypes = [
        ...uniqueTypes.map((type) => ({
          type: String(type),
          text: type === "Polygon" ? "Polygon" : type === "LineString" ? "Line" : "Point",
        })),
        { type: null, text: "Export all types as separate files" },
      ];

      this.exportFeaturesForm.get("geometryType").setValue(this.geometryTypes[0]);
    }
  }
}
